/**
 * Раскладка героев по платформам (E3): кто когда открывается и где стоит по умолчанию.
 *
 * Платформа открывается вместе со своим героем (волны из `HeroDef.unlockWave`: 1 / 5 / 10 / 20 / 30), шестая —
 * запас, открыта с первой волны и пустая (место под шестого героя v2 и для перестановки). Слоты — по порядку
 * открытия героев. Забежка (E4) собирает `RunState.heroes` через {@link rosterForWave}; бой не выпускает на поле
 * героя на закрытой платформе; платформы героев из состава считаются открытыми и после отката (ISSUE-60).
 */
import type { BalanceData, HeroDef, HeroId, PlatformSlot, RunHero } from "@newfungame/contracts";
import { PLATFORM_COUNT } from "@newfungame/contracts";

/** Волна открытия запасной (шестой) платформы. */
export const SPARE_PLATFORM_UNLOCK_WAVE = 1;

/** Герои в порядке открытия (при равной волне — в порядке данных). */
export function heroesInUnlockOrder(balance: BalanceData): HeroDef[] {
  return balance.heroes
    .map((h, i) => ({ h, i }))
    .sort((a, b) => a.h.unlockWave - b.h.unlockWave || a.i - b.i)
    .map((x) => x.h);
}

/** Волна открытия каждой из 6 платформ (индекс — слот). */
export function platformUnlockWaves(balance: BalanceData): number[] {
  const waves = heroesInUnlockOrder(balance)
    .slice(0, PLATFORM_COUNT)
    .map((h) => h.unlockWave);
  while (waves.length < PLATFORM_COUNT) waves.push(SPARE_PLATFORM_UNLOCK_WAVE);
  return waves;
}

/** Платформа героя по умолчанию — по порядку открытия. */
export function defaultSlot(balance: BalanceData, heroId: HeroId): PlatformSlot {
  const i = heroesInUnlockOrder(balance).findIndex((h) => h.id === heroId);
  return i < 0 ? PLATFORM_COUNT - 1 : Math.min(i, PLATFORM_COUNT - 1);
}

/** Герои, открытые к волне `wave`. */
export function unlockedHeroes(balance: BalanceData, wave: number): HeroId[] {
  return heroesInUnlockOrder(balance)
    .filter((h) => h.unlockWave <= wave)
    .map((h) => h.id);
}

/** Герои, которые открываются ровно на волне `wave` (для события `heroUnlocked`). */
export function heroesUnlockedAt(balance: BalanceData, wave: number): HeroId[] {
  return heroesInUnlockOrder(balance)
    .filter((h) => h.unlockWave === wave)
    .map((h) => h.id);
}

/** Уровень и звёзды героя из прогресса игрока. */
export interface HeroProgressLite {
  level: number;
  stars: number;
}

/**
 * Герои на платформах к волне `wave`: все открытые, уровни и звёзды — из `progress` (по умолчанию 1 и 0), слот —
 * из `slots` (перестановка игрока; на закрытую или занятую платформу не ставит) или по умолчанию.
 */
export function rosterForWave(
  balance: BalanceData,
  wave: number,
  progress: Partial<Record<HeroId, HeroProgressLite>> = {},
  slots: Partial<Record<HeroId, PlatformSlot>> = {},
): RunHero[] {
  const open = platformUnlockWaves(balance);
  const taken = new Set<number>();
  const out: RunHero[] = [];
  const ids = unlockedHeroes(balance, wave);
  // сначала явные перестановки, затем остальные по умолчанию
  for (const pass of [true, false]) {
    for (const heroId of ids) {
      if (out.some((r) => r.heroId === heroId)) continue;
      const wanted = slots[heroId];
      if (pass && wanted === undefined) continue;
      let slot = pass ? wanted! : defaultSlot(balance, heroId);
      if (!pass && taken.has(slot)) {
        const free = open.findIndex((w, s) => w <= wave && !taken.has(s));
        if (free < 0) continue;
        slot = free;
      }
      if ((open[slot] ?? Infinity) > wave || taken.has(slot)) continue;
      taken.add(slot);
      const p = progress[heroId];
      out.push({ heroId, slot, level: Math.max(1, p?.level ?? 1), stars: Math.max(0, Math.min(5, p?.stars ?? 0)) });
    }
  }
  return out.sort((a, b) => a.slot - b.slot);
}
